// stores/conferences.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import conferencesService from '@/services/conferences'

export const useConferencesStore = defineStore('conferences', () => {
	// ============ STATE ============
	const teacherConferences = ref([]) // сгруппированные по курсам
	const studentConferences = ref([]) // плоский список
	const courseConferences = ref({}) // { [courseId]: Conference[] }

	const loading = ref({
		teacher: false,
		student: false,
		course: {}, // { [courseId]: boolean }
	})

	const errors = ref({
		teacher: null,
		student: null,
		course: {}, // { [courseId]: string }
	})

	const lastFetched = ref({
		teacher: null,
		student: null,
		course: {}, // { [courseId]: timestamp }
	})

	// 🔥 НОВОЕ: Запоминаем параметры последнего запроса
	const lastFetchParams = ref({
		teacherIncludePast: null, // null = не загружали, true/false = загружали с этим параметром
		studentIncludePast: null,
		studentLimit: null,
	})

	const CACHE_TTL = 5 * 60 * 1000 // 5 минут

	/**
	 * Парсинг даты как локального времени
	 * scheduled_at приходит без таймзоны (2025-12-21T02:16:00)
	 */
	function parseLocalDate(dateStr) {
		if (!dateStr) return new Date(NaN)
		// Если уже есть Z или +/- таймзона - парсим как есть
		if (/[Z+-]\d{2}:?\d{2}$/.test(dateStr) || dateStr.endsWith('Z')) {
			return new Date(dateStr)
		}
		// Иначе парсим как локальное время через компоненты
		const [datePart, timePart] = dateStr.split('T')
		if (!datePart) return new Date(NaN)
		const [year, month, day] = datePart.split('-').map(Number)
		const [hour = 0, minute = 0, second = 0] = (timePart || '00:00:00')
			.split(':')
			.map(Number)
		return new Date(year, month - 1, day, hour, minute, second)
	}

	// ============ GETTERS ============

	const allTeacherConferences = computed(() =>
		teacherConferences.value.flatMap(group => group.conferences || [])
	)

	const liveConferencesCount = computed(
		() => allTeacherConferences.value.filter(c => c.status === 'live').length
	)

	const readyToStart = computed(() => {
		const now = Date.now()
		const fifteenMinutes = 15 * 60 * 1000
		return allTeacherConferences.value.filter(c => {
			if (c.status !== 'scheduled') return false
			const scheduledAt = parseLocalDate(c.scheduled_at).getTime()
			return scheduledAt - now <= fifteenMinutes
		})
	})

	const upcomingStudentConferences = computed(() => {
		const now = Date.now()
		return studentConferences.value
			.filter(c => {
				// Статус live - показываем
				if (c.status === 'live') return true
				// Отменённые/завершённые - не показываем
				if (
					c.status === 'cancelled' ||
					c.status === 'completed' ||
					c.status === 'ended'
				)
					return false
				const scheduled = parseLocalDate(c.scheduled_at).getTime()
				const duration = (c.duration_minutes || 60) * 60 * 1000
				// Будущие - показываем
				if (scheduled > now) return true
				// Идёт сейчас (scheduled <= now <= scheduled + duration)
				if (now <= scheduled + duration) return true
				return false
			})
			.sort(
				(a, b) =>
					parseLocalDate(a.scheduled_at).getTime() -
					parseLocalDate(b.scheduled_at).getTime()
			)
	})

	// ============ ACTIONS ============

	/**
	 * Загрузка конференций преподавателя
	 * 🔥 ИСПРАВЛЕНО: учитывает изменение параметра includePast
	 */
	async function fetchTeacherConferences(options = {}) {
		const { force = false, includePast = false } = options

		// 🔥 Проверяем, изменились ли параметры запроса
		const paramsChanged =
			lastFetchParams.value.teacherIncludePast !== includePast

		console.log('🔄 [ConferencesStore] fetchTeacherConferences:', {
			force,
			includePast,
			paramsChanged,
			lastIncludePast: lastFetchParams.value.teacherIncludePast,
			cachedGroups: teacherConferences.value.length,
		})

		// Используем кэш только если:
		// 1. force = false
		// 2. Параметры НЕ изменились
		// 3. Кэш не устарел
		// 4. Есть данные
		if (!force && !paramsChanged && lastFetched.value.teacher) {
			const elapsed = Date.now() - lastFetched.value.teacher
			if (elapsed < CACHE_TTL && teacherConferences.value.length > 0) {
				console.log('📦 [ConferencesStore] Using cached teacher conferences')
				return teacherConferences.value
			}
		}

		loading.value.teacher = true
		errors.value.teacher = null

		try {
			console.log(
				'🌐 [ConferencesStore] Fetching from API, includePast:',
				includePast
			)
			const data = await conferencesService.getTeacherConferences(includePast)

			teacherConferences.value = Array.isArray(data) ? data : []
			lastFetched.value.teacher = Date.now()

			// 🔥 Сохраняем параметры запроса
			lastFetchParams.value.teacherIncludePast = includePast

			console.log(
				'✅ [ConferencesStore] Teacher conferences loaded:',
				teacherConferences.value.length,
				'groups, includePast:',
				includePast
			)

			// 🔥 Отладка: показываем что загрузилось
			teacherConferences.value.forEach(group => {
				console.log(
					`  📚 ${group.course_title}: ${
						group.conferences?.length || 0
					} conferences`
				)
				group.conferences?.slice(0, 3).forEach(c => {
					console.log(`    - ${c.title} (${c.status}) @ ${c.scheduled_at}`)
				})
			})

			return teacherConferences.value
		} catch (err) {
			console.error(
				'❌ [ConferencesStore] Failed to fetch teacher conferences:',
				err
			)
			lastFetched.value.teacher = null
			errors.value.teacher =
				err.response?.data?.detail ||
				'Не удалось загрузить конференции преподавателя'
			throw err
		} finally {
			loading.value.teacher = false
		}
	}

	/**
	 * Загрузка конференций студента
	 * 🔥 ИСПРАВЛЕНО: учитывает изменение параметров
	 */
	async function fetchStudentConferences(options = {}) {
		const { force = false, limit = null, includePast = false } = options

		// 🔥 Проверяем, изменились ли параметры запроса
		const paramsChanged =
			lastFetchParams.value.studentIncludePast !== includePast ||
			lastFetchParams.value.studentLimit !== limit

		console.log('🔄 [ConferencesStore] fetchStudentConferences:', {
			force,
			limit,
			includePast,
			paramsChanged,
		})

		if (!force && !paramsChanged && lastFetched.value.student) {
			const elapsed = Date.now() - lastFetched.value.student
			if (elapsed < CACHE_TTL && studentConferences.value.length > 0) {
				console.log('📦 [ConferencesStore] Using cached student conferences')
				return studentConferences.value
			}
		}

		loading.value.student = true
		errors.value.student = null

		try {
			console.log(
				'🌐 [ConferencesStore] Fetching student conferences from API...',
				{
					limit,
					includePast,
				}
			)
			const data = await conferencesService.getMy(limit, includePast)
			studentConferences.value = Array.isArray(data) ? data : []
			lastFetched.value.student = Date.now()

			// 🔥 Сохраняем параметры запроса
			lastFetchParams.value.studentIncludePast = includePast
			lastFetchParams.value.studentLimit = limit

			console.log(
				'✅ [ConferencesStore] Student conferences loaded:',
				studentConferences.value.length
			)
			return studentConferences.value
		} catch (err) {
			console.error(
				'❌ [ConferencesStore] Failed to fetch student conferences:',
				err
			)
			lastFetched.value.student = null
			errors.value.student =
				err.response?.data?.detail || 'Не удалось загрузить конференции'
			throw err
		} finally {
			loading.value.student = false
		}
	}

	/**
	 * Загрузка конференций конкретного курса
	 */
	async function fetchCourseConferences(courseId, options = {}) {
		const { force = false, includePast = false } = options

		if (!force && lastFetched.value.course[courseId]) {
			const elapsed = Date.now() - lastFetched.value.course[courseId]
			if (
				elapsed < CACHE_TTL &&
				courseConferences.value[courseId]?.length > 0
			) {
				console.log(
					`📦 [ConferencesStore] Using cached conferences for course ${courseId}`
				)
				return courseConferences.value[courseId]
			}
		}

		loading.value.course = { ...loading.value.course, [courseId]: true }
		errors.value.course = { ...errors.value.course, [courseId]: null }

		try {
			console.log(
				`🔄 [ConferencesStore] Fetching conferences for course ${courseId}...`
			)
			const data = await conferencesService.getByCourseId(courseId, includePast)
			courseConferences.value = {
				...courseConferences.value,
				[courseId]: Array.isArray(data) ? data : [],
			}
			lastFetched.value.course = {
				...lastFetched.value.course,
				[courseId]: Date.now(),
			}
			console.log(
				`✅ [ConferencesStore] Course ${courseId} conferences loaded:`,
				courseConferences.value[courseId].length
			)
			return courseConferences.value[courseId]
		} catch (err) {
			console.error(
				`❌ [ConferencesStore] Failed to fetch course ${courseId} conferences:`,
				err
			)
			lastFetched.value.course = {
				...lastFetched.value.course,
				[courseId]: null,
			}
			errors.value.course = {
				...errors.value.course,
				[courseId]: err.response?.data?.detail || 'Ошибка загрузки',
			}
			throw err
		} finally {
			loading.value.course = { ...loading.value.course, [courseId]: false }
		}
	}

	/**
	 * Получить конференции курса (computed)
	 */
	function getConferencesByCourseId(courseId) {
		return computed(() => courseConferences.value[courseId] || [])
	}

	/**
	 * Создать конференцию
	 */
	async function createConference(courseId, conferenceData) {
		console.log(
			'🔄 [ConferencesStore] Creating conference for course',
			courseId
		)
		const response = await conferencesService.create(courseId, conferenceData)
		invalidateCourse(courseId)
		invalidateTeacher()
		console.log('✅ [ConferencesStore] Conference created:', response.id)
		return response
	}

	/**
	 * Обновить конференцию
	 */
	async function updateConference(conferenceId, conferenceData) {
		console.log('🔄 [ConferencesStore] Updating conference', conferenceId)
		const response = await conferencesService.update(
			conferenceId,
			conferenceData
		)
		invalidateAll()
		console.log('✅ [ConferencesStore] Conference updated')
		return response
	}

	/**
	 * Удалить конференцию
	 */
	async function deleteConference(conferenceId, courseId = null) {
		console.log('🔄 [ConferencesStore] Deleting conference', conferenceId)
		await conferencesService.delete(conferenceId)

		// Удаляем из кэша курса
		if (courseId && courseConferences.value[courseId]) {
			courseConferences.value = {
				...courseConferences.value,
				[courseId]: courseConferences.value[courseId].filter(
					c => c.id !== conferenceId
				),
			}
		}

		// Удаляем из кэша преподавателя
		teacherConferences.value = teacherConferences.value
			.map(group => ({
				...group,
				conferences: (group.conferences || []).filter(
					c => c.id !== conferenceId
				),
			}))
			.filter(group => group.conferences?.length > 0)

		// Удаляем из кэша студента
		studentConferences.value = studentConferences.value.filter(
			c => c.id !== conferenceId
		)

		console.log('✅ [ConferencesStore] Conference deleted')
	}

	/**
	 * Начать конференцию
	 */
	async function startConference(conferenceId) {
		console.log('🔄 [ConferencesStore] Starting conference', conferenceId)
		const response = await conferencesService.start(conferenceId)
		updateConferenceStatus(conferenceId, 'live')
		console.log('✅ [ConferencesStore] Conference started')
		return response
	}

	/**
	 * Завершить конференцию
	 */
	async function endConference(conferenceId) {
		console.log('🔄 [ConferencesStore] Ending conference', conferenceId)
		const response = await conferencesService.end(conferenceId)
		updateConferenceStatus(conferenceId, 'completed')
		console.log('✅ [ConferencesStore] Conference ended')
		return response
	}

	/**
	 * Отменить конференцию
	 */
	async function cancelConference(conferenceId) {
		console.log('🔄 [ConferencesStore] Cancelling conference', conferenceId)
		const response = await conferencesService.cancel(conferenceId)
		updateConferenceStatus(conferenceId, 'cancelled')
		console.log('✅ [ConferencesStore] Conference cancelled')
		return response
	}

	/**
	 * Присоединиться к конференции
	 */
	async function joinConference(conferenceId) {
		console.log('🔄 [ConferencesStore] Joining conference', conferenceId)
		const tokenData = await conferencesService.join(conferenceId)
		console.log('✅ [ConferencesStore] Joined conference, token received')
		return tokenData
	}

	/**
	 * Покинуть конференцию
	 */
	async function leaveConference(conferenceId) {
		console.log('🔄 [ConferencesStore] Leaving conference', conferenceId)
		await conferencesService.leave(conferenceId)
		console.log('✅ [ConferencesStore] Left conference')
	}

	// ============ HELPERS ============

	/**
	 * Обновить данные конференции во всех кэшах
	 */
	function applyConferenceUpdate(conferenceId, patch = {}) {
		const targetId = String(conferenceId)
		const applyPatch = conf =>
			String(conf.id) === targetId ? { ...conf, ...patch } : conf

		// Обновляем в кэше преподавателя
		teacherConferences.value = teacherConferences.value.map(group => ({
			...group,
			conferences: (group.conferences || []).map(applyPatch),
		}))

		// Обновляем в кэше студента
		studentConferences.value = studentConferences.value.map(applyPatch)

		// Обновляем в кэше курсов
		courseConferences.value = Object.fromEntries(
			Object.entries(courseConferences.value).map(([cid, list]) => [
				cid,
				list.map(applyPatch),
			])
		)
	}

	/**
	 * Обновить статус конференции во всех кэшах
	 */
	function updateConferenceStatus(conferenceId, status) {
		applyConferenceUpdate(conferenceId, { status })
	}

	/**
	 * Инвалидировать кэш курса
	 */
	function invalidateCourse(courseId) {
		lastFetched.value.course = { ...lastFetched.value.course, [courseId]: null }
	}

	/**
	 * Инвалидировать кэш преподавателя
	 */
	function invalidateTeacher() {
		lastFetched.value.teacher = null
		lastFetchParams.value.teacherIncludePast = null
	}

	/**
	 * Инвалидировать кэш студента
	 */
	function invalidateStudent() {
		lastFetched.value.student = null
		lastFetchParams.value.studentIncludePast = null
		lastFetchParams.value.studentLimit = null
	}

	/**
	 * Инвалидировать все кэши
	 */
	function invalidateAll() {
		lastFetched.value = { teacher: null, student: null, course: {} }
		lastFetchParams.value = {
			teacherIncludePast: null,
			studentIncludePast: null,
			studentLimit: null,
		}
	}

	// ============ RETURN ============
	return {
		// State
		teacherConferences,
		studentConferences,
		courseConferences,
		loading,
		errors,
		lastFetchParams, // 🔥 Экспортируем для отладки

		// Getters
		allTeacherConferences,
		liveConferencesCount,
		readyToStart,
		upcomingStudentConferences,
		getConferencesByCourseId,

		// Actions
		fetchTeacherConferences,
		fetchStudentConferences,
		fetchCourseConferences,
		createConference,
		updateConference,
		deleteConference,
		startConference,
		endConference,
		cancelConference,
		joinConference,
		leaveConference,
		applyConferenceUpdate,

		// Helpers
		invalidateCourse,
		invalidateTeacher,
		invalidateStudent,
		invalidateAll,
	}
})
